import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { X, Search, Plus, UserCheck, FilePlus, Trash2, AlertTriangle } from 'lucide-react';
import { listProduttori, createProduttore } from '../api/produttori';
import { apiErrorMessage } from '../lib/apiError';
import { cambiaProduttoreRichiesta } from '../api/richieste';
import type { CustomerDetail, RichiestaDetail } from '../api/richieste';
import type { Produttore } from '../types/produttore';
import { produttoreCreateSchema, type ProduttoreCreateData } from '../schemas/produttore';
import UsaDatiClienteForm from './UsaDatiClienteForm';
import { useDebounce } from '../hooks/useDebounce';

// ── Helpers ──────────────────────────────────────────────────────────────────

const inputCls =
  'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

// 4 intenzioni esplicite (Modello C): scegli esistente, crea nuovo, usa i dati
// del cliente (materializza), rimuovi. La vecchia "Produttore = Cliente" che
// azzerava produttore_id (stato che bloccava l'omologa) è stata rimossa.
type Mode = 'esistente' | 'nuovo' | 'cliente' | 'rimuovi';

// ── Props ─────────────────────────────────────────────────────────────────────

interface CambiaProduttoreModalProps {
  richiestaId: string;
  customer: CustomerDetail;
  onClose: () => void;
  onSaved: (updated: RichiestaDetail) => void;
}

// ── Modal ─────────────────────────────────────────────────────────────────────

export default function CambiaProduttoreModal({
  richiestaId,
  customer,
  onClose,
  onSaved,
}: CambiaProduttoreModalProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('esistente');

  // autocomplete state (mode "esistente")
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const [suggestions, setSuggestions] = useState<Produttore[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedProduttore, setSelectedProduttore] = useState<Produttore | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  // "rimuovi" mode state
  const [removeCoincidesChoice, setRemoveCoincidesChoice] = useState<'coincide' | 'non_identificato'>('coincide');

  // "nuovo" mode state
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // general save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // RHF for create form (mode "nuovo")
  const {
    register,
    handleSubmit,
    formState: { errors: createErrors },
  } = useForm<ProduttoreCreateData>({
    resolver: zodResolver(produttoreCreateSchema),
    defaultValues: {},
  });

  // Fetch suggestions on debounced query (solo mode "esistente")
  useEffect(() => {
    void (async () => {
      if (!debouncedQuery.trim() || mode !== 'esistente') {
        setSuggestions([]);
        return;
      }
      setLoadingSuggestions(true);
      try {
        const res = await listProduttori({ search: debouncedQuery, limit: 10 });
        setSuggestions(res.items);
      } catch {
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    })();
  }, [debouncedQuery, mode]);

  // Close dropdown on outside click
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function changeMode(next: Mode) {
    setMode(next);
    setSaveError(null);
    setCreateError(null);
  }

  function selectSuggestion(p: Produttore) {
    setSelectedProduttore(p);
    setQuery(p.ragione_sociale);
    setShowDropdown(false);
    setSuggestions([]);
  }

  // Associa un produttore esistente selezionato.
  async function handleSaveEsistente() {
    if (!selectedProduttore) return;
    setSaveError(null);
    setIsSaving(true);
    try {
      const updated = await cambiaProduttoreRichiesta(richiestaId, {
        produttore_id: selectedProduttore.id,
        produttore_coincide: false,
      });
      onSaved(updated);
    } catch {
      setSaveError(t('richieste.cambia_produttore.errore_salvataggio'));
    } finally {
      setIsSaving(false);
    }
  }

  // Crea un nuovo produttore e lo associa subito.
  async function onCreateSubmit(data: ProduttoreCreateData) {
    setIsCreating(true);
    setCreateError(null);
    try {
      const newProd = await createProduttore({
        ragione_sociale: data.ragione_sociale,
        piva: data.piva?.trim() || null,
        codice_fiscale: data.codice_fiscale?.trim() || null,
        email: data.email?.trim() || null,
        telefono: data.telefono?.trim() || null,
        indirizzo: data.indirizzo?.trim() || null,
        citta: data.citta?.trim() || null,
        provincia: data.provincia?.trim() || null,
        cap: data.cap?.trim() || null,
        note: data.note?.trim() || null,
        tipo_soggetto: (data.tipo_soggetto as 'azienda' | 'privato' | 'ente_pubblico' | null) || null,
      });
      const updated = await cambiaProduttoreRichiesta(richiestaId, {
        produttore_id: newProd.id,
        produttore_coincide: false,
      });
      onSaved(updated);
    } catch (err: unknown) {
      const s = (err as { response?: { status?: number } })?.response?.status;
      if (s === 409) {
        setCreateError(t('richieste.cambia_produttore.errore_piva_409'));
      } else {
        // T1 sul CF vale anche qui: il 422 ha un detail parlante (stringa o
        // array Pydantic) — `apiErrorMessage` lo estrae invece di scartarlo.
        setCreateError(apiErrorMessage(err, t('richieste.cambia_produttore.errore_creazione')));
      }
    } finally {
      setIsCreating(false);
    }
  }

  async function handleRimuovi() {
    setSaveError(null);
    setIsSaving(true);
    try {
      const updated = await cambiaProduttoreRichiesta(richiestaId, {
        produttore_id: null,
        produttore_coincide: removeCoincidesChoice === 'coincide',
      });
      onSaved(updated);
    } catch {
      setSaveError(t('richieste.cambia_produttore.errore_rimozione'));
    } finally {
      setIsSaving(false);
    }
  }

  const identityStr = (p: Produttore) => {
    if (p.piva) return t('richieste.cambia_produttore.piva_label', { val: p.piva });
    if (p.codice_fiscale) return t('richieste.cambia_produttore.cf_label', { val: p.codice_fiscale });
    return '';
  };

  const modeBtn = (m: Mode, label: string, Icon: typeof UserCheck) => (
    <button
      type="button"
      onClick={() => changeMode(m)}
      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
        mode === m
          ? m === 'rimuovi'
            ? 'bg-accent-danger text-white border-accent-danger'
            : 'bg-sage text-white border-sage'
          : 'bg-white text-ink-mid border-stone-border hover:bg-stone-bg'
      }`}
    >
      <Icon size={13} /> {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="modal-cambia-produttore">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />

      {/* Modal content */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-[600px] mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark">{t('richieste.cambia_produttore.titolo')}</h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Selettore intenzione (4 voci) */}
          <div className="flex flex-wrap gap-2">
            {modeBtn('esistente', t('richieste.cambia_produttore.mode_esistente'), Search)}
            {modeBtn('nuovo', t('richieste.cambia_produttore.mode_nuovo'), FilePlus)}
            {modeBtn('cliente', t('richieste.cambia_produttore.mode_cliente'), UserCheck)}
            {modeBtn('rimuovi', t('richieste.cambia_produttore.mode_rimuovi'), Trash2)}
          </div>

          {/* ── Mode: scegli esistente ── */}
          {mode === 'esistente' && (
            <div>
              <label className="block text-xs text-ink-soft mb-1">{t('richieste.cambia_produttore.cerca_label')}</label>
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setSelectedProduttore(null);
                      setShowDropdown(true);
                    }}
                    onFocus={() => query && setShowDropdown(true)}
                    placeholder={t('richieste.cambia_produttore.cerca_placeholder')}
                    className={`${inputCls} pl-8`}
                  />
                </div>

                {showDropdown && query.trim().length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-stone-border rounded-lg shadow-lg max-h-72 overflow-y-auto">
                    {loadingSuggestions ? (
                      <div className="px-3 py-2.5 text-sm text-ink-soft">{t('richieste.cambia_produttore.ricerca_in_corso')}</div>
                    ) : suggestions.length === 0 ? (
                      <div className="px-3 py-2.5 text-sm text-ink-faint">{t('richieste.cambia_produttore.nessun_risultato')}</div>
                    ) : (
                      <ul>
                        {suggestions.map((p) => (
                          <li key={p.id}>
                            <button
                              type="button"
                              onClick={() => selectSuggestion(p)}
                              className="w-full text-left px-3 py-2.5 hover:bg-stone-bg transition-colors"
                            >
                              <div className="text-sm font-medium text-ink-dark">{p.ragione_sociale}</div>
                              {identityStr(p) && (
                                <div className="text-xs text-ink-soft font-mono">{identityStr(p)}</div>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              {selectedProduttore && (
                <p className="mt-1.5 text-xs text-accent-success font-medium">
                  {t('richieste.cambia_produttore.selezionato')} {selectedProduttore.ragione_sociale}
                  {identityStr(selectedProduttore) ? ` — ${identityStr(selectedProduttore)}` : ''}
                </p>
              )}
            </div>
          )}

          {/* ── Mode: crea nuovo ── */}
          {mode === 'nuovo' && (
            <div className="space-y-3">
              {createError && (
                <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
                  {createError}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs text-ink-soft mb-1">{t('richieste.usa_dati_cliente.ragione_sociale')}</label>
                  <input className={inputCls} {...register('ragione_sociale')} />
                  {createErrors.ragione_sociale && (
                    <p className="text-xs text-accent-danger mt-0.5">{createErrors.ragione_sociale.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-ink-soft mb-1">{t('richieste.usa_dati_cliente.partita_iva')}</label>
                  <input className={inputCls} {...register('piva')} maxLength={11} placeholder={t('richieste.usa_dati_cliente.piva_placeholder')} />
                  {createErrors.piva && (
                    <p className="text-xs text-accent-danger mt-0.5">{createErrors.piva.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-ink-soft mb-1">{t('richieste.usa_dati_cliente.codice_fiscale')}</label>
                  <input className={inputCls} {...register('codice_fiscale')} maxLength={16} />
                  {createErrors.codice_fiscale && (
                    <p className="text-xs text-accent-danger mt-0.5">{createErrors.codice_fiscale.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-ink-soft mb-1">{t('richieste.usa_dati_cliente.email')}</label>
                  <input className={inputCls} type="email" {...register('email')} />
                </div>
                <div>
                  <label className="block text-xs text-ink-soft mb-1">{t('richieste.usa_dati_cliente.telefono')}</label>
                  <input className={inputCls} {...register('telefono')} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-ink-soft mb-1">{t('richieste.usa_dati_cliente.tipo_soggetto')}</label>
                  <select className={inputCls} {...register('tipo_soggetto')}>
                    <option value="">{t('richieste.usa_dati_cliente.tipo_seleziona')}</option>
                    <option value="azienda">{t('richieste.usa_dati_cliente.tipo_azienda')}</option>
                    <option value="privato">{t('richieste.usa_dati_cliente.tipo_privato')}</option>
                    <option value="ente_pubblico">{t('richieste.usa_dati_cliente.tipo_ente')}</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ── Mode: usa i dati del cliente (materializza) ── */}
          {mode === 'cliente' && (
            <UsaDatiClienteForm
              richiestaId={richiestaId}
              customer={customer}
              onMaterialized={onSaved}
            />
          )}

          {/* ── Mode: rimuovi ── */}
          {mode === 'rimuovi' && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 px-3 py-2 bg-accent-warning-bg border border-[#FDE68A] rounded text-xs text-accent-warning">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{t('richieste.cambia_produttore.rimuovi_avviso')}</span>
              </div>
              <p className="text-sm text-ink-mid">
                {t('richieste.cambia_produttore.rimuovi_istruzione')}
              </p>
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="radio" name="removeChoice" value="coincide"
                    checked={removeCoincidesChoice === 'coincide'}
                    onChange={() => setRemoveCoincidesChoice('coincide')}
                    className="mt-0.5 accent-sage" />
                  <div>
                    <div className="text-sm font-medium text-ink-dark">{t('richieste.cambia_produttore.coincide_titolo')}</div>
                    <div className="text-xs text-ink-soft">{t('richieste.cambia_produttore.coincide_desc')}</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="radio" name="removeChoice" value="non_identificato"
                    checked={removeCoincidesChoice === 'non_identificato'}
                    onChange={() => setRemoveCoincidesChoice('non_identificato')}
                    className="mt-0.5 accent-sage" />
                  <div>
                    <div className="text-sm font-medium text-ink-dark">{t('richieste.cambia_produttore.non_id_titolo')}</div>
                    <div className="text-xs text-ink-soft">{t('richieste.cambia_produttore.non_id_desc')}</div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Save error */}
          {saveError && (
            <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
              {saveError}
            </div>
          )}
        </div>

        {/* Footer — azione dipende dalla modalità ('cliente' ha il submit nel form) */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
          <button type="button" onClick={onClose} disabled={isSaving || isCreating}
            className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
            {t('common.azioni.annulla')}
          </button>
          {mode === 'esistente' && (
            <button type="button" onClick={handleSaveEsistente} disabled={isSaving || !selectedProduttore}
              className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {isSaving ? t('richieste.cambia_produttore.salvataggio') : t('richieste.cambia_produttore.salva')}
            </button>
          )}
          {mode === 'nuovo' && (
            <button type="button" onClick={handleSubmit(onCreateSubmit)} disabled={isCreating}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60">
              <Plus size={14} /> {isCreating ? t('richieste.cambia_produttore.creazione') : t('richieste.cambia_produttore.crea_associa')}
            </button>
          )}
          {mode === 'rimuovi' && (
            <button type="button" onClick={handleRimuovi} disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-accent-danger rounded-lg hover:bg-[#B91C1C] transition-colors disabled:opacity-60">
              {isSaving ? t('richieste.cambia_produttore.rimozione') : t('richieste.cambia_produttore.rimuovi_btn')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
